import React from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

const PageLayout = ({ title, children }) => (
  <div className="pt-40 pb-24 bg-white min-h-screen">
    <div className="max-w-4xl mx-auto px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <span className="text-accent-orange font-bold tracking-[0.3em] uppercase text-[10px] bg-accent-orange/10 px-4 py-1.5 rounded-full">Legal Information</span>
        <h1 className="text-5xl md:text-7xl font-serif font-black text-black mt-8 mb-16 uppercase tracking-tighter leading-none">{title}</h1>
        <div className="prose prose-lg max-w-none text-black/70 space-y-12 leading-relaxed">
          {children}
        </div>
      </motion.div>
    </div>
  </div>
);

export const Privacy = () => (
  <PageLayout title="Privacy Policy">
    <section>
      <h2 className="text-2xl font-serif font-black text-black mb-6 uppercase tracking-tight">1. Information Collection</h2>
      <p>We collect information you provide directly to us when inquiring about our rental units at 7601 Canada Avenue. This includes your name, email address, phone number, and any details regarding your stay. We also automatically collect certain technical information when you visit our website, such as your IP address and browsing behavior, to improve our user experience.</p>
    </section>
    <section>
      <h2 className="text-2xl font-serif font-black text-black mb-6 uppercase tracking-tight">2. Use of Information</h2>
      <p>The information we collect is used solely to facilitate your stay and improve our services. This includes:</p>
      <ul className="list-disc pl-6 mt-6 space-y-4">
        <li>Responding to your inquiries and booking requests.</li>
        <li>Providing customer support and maintenance updates.</li>
        <li>Sending important notices about your rental agreement.</li>
        <li>Complying with local rental regulations and legal requirements.</li>
      </ul>
    </section>
    <section>
      <h2 className="text-2xl font-serif font-black text-black mb-6 uppercase tracking-tight">3. Data Sharing and Security</h2>
      <p>We do not sell or rent your personal information to third parties. We may share information with trusted service providers who assist us in operating our website or conducting our business, provided they agree to keep this information confidential. We implement industry-standard security measures to protect your data from unauthorized access.</p>
    </section>
    <section>
      <h2 className="text-2xl font-serif font-black text-black mb-6 uppercase tracking-tight">4. Cookies and Tracking</h2>
      <p>Our website uses cookies to enhance functionality and analyze traffic. You can choose to disable cookies through your browser settings, though this may affect the performance of certain features on the site.</p>
    </section>
    <p className="text-xs font-bold uppercase tracking-widest text-black/30 pt-12 border-t border-black/5">Last Updated: October 2023</p>
  </PageLayout>
);

export const Terms = () => (
  <PageLayout title="Terms of Service">
    <section>
      <h2 className="text-2xl font-serif font-black text-black mb-6 uppercase tracking-tight">1. Acceptance of Terms</h2>
      <p>By using the 7601 Canada website and inquiring about our suites, you agree to be bound by these Terms of Service. If you do not agree, please do not use our services.</p>
    </section>
    <section>
      <h2 className="text-2xl font-serif font-black text-black mb-6 uppercase tracking-tight">2. Rental Use and Restrictions</h2>
      <p>Our units are designated for short to mid-term residential use. Commercial activities, large parties, and illegal acts are strictly prohibited. Guests must adhere to the maximum occupancy limits (6 guests per suite) and respect the quiet hours of the complex.</p>
    </section>
    <section>
      <h2 className="text-2xl font-serif font-black text-black mb-6 uppercase tracking-tight">3. Booking and Cancellations</h2>
      <p>All bookings are subject to availability. Specific cancellation policies will be outlined in your individual rental agreement provided during the inquiry process. We reserve the right to refuse service to anyone for any reason at any time.</p>
    </section>
    <section>
      <h2 className="text-2xl font-serif font-black text-black mb-6 uppercase tracking-tight">4. Liability</h2>
      <p>7601 Canada and its management shall not be liable for any indirect, incidental, or consequential damages arising from your stay. Guests are responsible for any damages caused to the property during their occupancy.</p>
    </section>
  </PageLayout>
);

export const Accessibility = () => (
  <PageLayout title="Accessibility Statement">
    <section>
      <h2 className="text-2xl font-serif font-black text-black mb-6 uppercase tracking-tight">Our Commitment to Inclusion</h2>
      <p>7601 Canada is dedicated to providing a digital experience that is accessible to everyone, regardless of ability. We strive to meet the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards to ensure our website is usable for all visitors.</p>
    </section>
    <section>
      <h2 className="text-2xl font-serif font-black text-black mb-6 uppercase tracking-tight">Accessibility Features</h2>
      <p>We have integrated several features to improve accessibility, including:</p>
      <ul className="list-disc pl-6 mt-6 space-y-4 font-medium">
        <li>A dedicated ADA widget for real-time adjustments to text size and contrast.</li>
        <li>Logical heading structures and semantic HTML for screen reader compatibility.</li>
        <li>High-contrast color schemes (Black & White) for optimal readability.</li>
        <li>Full keyboard navigation support.</li>
        <li>Descriptive alt text for all critical interface elements.</li>
      </ul>
    </section>
    <section>
      <h2 className="text-2xl font-serif font-black text-black mb-6 uppercase tracking-tight">Ongoing Efforts</h2>
      <p>Accessibility is an ongoing priority. We regularly audit our site to identify and resolve potential barriers. If you encounter any issues or have suggestions for improvement, please contact us at <span className="text-accent-orange font-bold">stay@7601canada.com</span>.</p>
    </section>
  </PageLayout>
);

const Legal = () => {
  const location = useLocation();
  const path = location.pathname;

  if (path === '/terms') return <Terms />;
  if (path === '/accessibility-statement') return <Accessibility />;
  
  return <Privacy />;
};

export default Legal;